import { z } from "zod";
import { BLOCK_TYPES, CALLOUT_VARIANTS, type BlockType } from "../blocks/registry";

/**
 * Schemas de saída dos estágios do gerador (E5).
 *
 * São a fronteira de confiança entre o modelo e o resto do sistema: a saída de
 * um LLM é entrada não confiável como qualquer outra. Validar aqui é o que
 * impede um campo faltando ou um tipo inesperado de virar `undefined` silencioso
 * três camadas adiante.
 *
 * O mesmo schema serve de `output_config.format` na chamada (saída estruturada)
 * e de validação defensiva na volta — o backend valida e o front revalida antes
 * de aplicar no editor. Redundante de propósito: a checagem é barata e o erro
 * que ela evita é caro.
 */

const naoVazio = (campo: string) => z.string().trim().min(1, `${campo} não pode ficar vazio`);

// ── Estágio 1: briefing ─────────────────────────────────────────────────────

const KNOWN_BLOCKS = new Set<string>(BLOCK_TYPES);

/**
 * Sugestão, não contrato: o modelo já devolveu nome inventado
 * ("tabela-comparativa" no lugar de `table-compare`) e isso derrubava o
 * briefing inteiro. Nome fora do catálogo é descartado; o resto passa.
 */
const suggestedBlocksSchema = z
  .array(z.string())
  .default([])
  .transform(values => [
    ...new Set(values.filter((value): value is BlockType => KNOWN_BLOCKS.has(value))),
  ]);

export const briefSchema = z.object({
  primaryKeyword: naoVazio("keyword primária"),
  secondaryKeywords: z.array(z.string().trim().min(1)).max(8),
  searchIntent: z.enum(["informacional", "comercial", "transacional", "navegacional"]),
  funnel: z.enum(["TOFU", "MOFU", "BOFU"]),
  /** O recorte que só a Fidexa pode dar. É o campo que evita post genérico. */
  angle: naoVazio("ângulo"),
  outline: z
    .array(
      z.object({
        h2: naoVazio("H2"),
        /** A resposta direta que a primeira frase do H2 precisa entregar. */
        directAnswer: naoVazio("resposta direta"),
        h3: z.array(z.string().trim().min(1)).max(6).default([]),
      })
    )
    .min(4)
    .max(8),
  faqQuestions: z.array(naoVazio("pergunta")).min(4).max(6),
  suggestedBlocks: suggestedBlocksSchema,
  internalLinkTargets: z
    .array(z.object({ slug: naoVazio("slug"), reason: naoVazio("motivo") }))
    .default([]),
  sourcesToCite: z.array(z.object({ claim: naoVazio("afirmação"), source: naoVazio("fonte") })).default([]),
  estimatedWords: z.number().int().min(900).max(2200),
});

export type BriefOutput = z.infer<typeof briefSchema>;

// ── Estágio 2: rascunho ─────────────────────────────────────────────────────

export const draftSchema = z.object({
  /** HTML restrito à allowlist do sanitizador — ver `buildHtmlContract()`. */
  contentHtml: naoVazio("conteúdo"),
  wordCount: z.number().int().positive(),
});

export type DraftOutput = z.infer<typeof draftSchema>;

// ── Estágio 3: pacote SEO ───────────────────────────────────────────────────

export const seoSchema = z.object({
  /**
   * Título do post (H1) sugerido. Opcional: o backend anterior à Fase C não o
   * devolve. O limite de 70 é aviso no checklist, não motivo para derrubar o
   * pacote inteiro — por isso o teto aqui é folgado.
   */
  title: z.string().trim().min(1).max(120).optional(),
  metaTitle: z.string().trim().min(1).max(60, "meta title passa de 60 caracteres"),
  metaDescription: z.string().trim().min(1).max(155, "meta description passa de 155 caracteres"),
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug deve ser minúsculo, sem acento, separado por hífen"),
  excerpt: z.string().trim().min(1).max(300),
  tags: z.array(z.string().trim().min(1)).max(8),
  faq: z
    .array(z.object({ question: naoVazio("pergunta"), answer: naoVazio("resposta") }))
    .min(4)
    .max(6),
  imageAlts: z.array(z.object({ src: naoVazio("src"), alt: naoVazio("alt") })).default([]),
});

export type SeoOutput = z.infer<typeof seoSchema>;

// ── Estágio 0: organização (Fase C) ───────────────────────────────────────────

/**
 * Onde o post entra: cluster, papel, categoria e autor. O notifyme restringe
 * `clusterSlug` e `authorSlug` aos slugs reais (+ "none") com saída
 * estruturada; aqui só se confere a forma — quem resolve slug → id é o painel,
 * com as listas que ele já carregou, e slug desconhecido é ignorado.
 */
export const organizeSchema = z.object({
  clusterSlug: z.string().trim(),
  role: z.enum(["pillar", "satellite", "none"]),
  category: z.string().trim().default(""),
  authorSlug: z.string().trim(),
  rationale: z.string().trim().default(""),
});

export type OrganizeOutput = z.infer<typeof organizeSchema>;

export const calloutVariantSchema = z.enum(CALLOUT_VARIANTS);

/**
 * Modelo sugerido por estágio. A decisão final é do backend (a chamada roda
 * lá), mas a recomendação mora junto do prompt porque depende dele: briefing e
 * SEO são estruturados e curtos; redação é o estágio em que a qualidade do
 * texto é o produto.
 */
export const STAGE_MODEL = {
  organize: "claude-opus-5",
  brief: "claude-sonnet-5",
  draft: "claude-opus-5",
  seo: "claude-sonnet-5",
} as const;
